import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import { BillQuery } from '../bill/bill.query';
import { GetBillOption } from '../bill/types';
import { StatusState, StatusStore } from './status.store';
import { JobState } from './types';

@Injectable({ providedIn: 'root' })
export class StatusQuery extends Query<StatusState> {
  isLoading$ = this.selectLoading();
  jobs$ = this.select((status) => status?.job?.items);
  jobCount$ = this.select((status) => status.job.count);
  totalJobCount$ = this.select((status) => status.job.totalCount);
  firstBillInProgress$ = this.isFirstBillInProgress();
  isPollingComplete$ = this.select('ui').pipe(map((ui) => !!ui.isPollingComplete));
  latestJob$ = this.getLatestJob();
  latestJobId$ = this.select((status) => status?.latestJobId);
  latestJobId = this.getValue().latestJobId;

  constructor(protected store: StatusStore, private billQuery: BillQuery) {
    super(store);
  }
  
  get latestJob() {
    const jobs = this.getValue().job?.items;
    if (!jobs?.length) return null;
    return jobs.length > 0 ? jobs[0] : null;
  }

  getLatestJob() {
    return this.select('job').pipe(
      map((job) => {
        const jobs = job?.items;
        if (!jobs?.length) return null;
        return jobs.length > 0 ? jobs[0] : null;
      }),
    );
  }

  get jobMeta(){
    return this.getValue().jobMeta;
  }

  get jobsOnProgress() {
    const jobs = this.getValue().job?.items;
    return jobs.filter((job) => job.state === JobState.ONPROGRESS);
  }

  get jobsOnProgress$() {
    return this.jobs$.pipe(
      map((jobs) => jobs?.filter((job) => job.state === JobState.ONPROGRESS)),
      distinctUntilChanged(),
    );
  }

  /**
   * Gets whether current job is on progress
   * based upon latestJobId stored in store
   * after adding job in queue.
   */
  get isCurrentJobOnProgress$() {
    return this.jobs$.pipe(
      mergeMap((jobs) =>
        this.latestJobId$.pipe(
          map((latestJobId) => {
            return { jobs, latestJobId };
          }),
        ),
      ),
      map(({ jobs, latestJobId }) => jobs.filter((job) => latestJobId == job.id)),
      map((jobs) => {
        if (jobs.length == 0) {
          return { jobsLoaded: false, jobOnprogress: false };
        } else if (jobs.length && jobs[0].state === JobState.ONPROGRESS) {
          return { jobsLoaded: true, jobOnprogress: true };
        } else {
          return { jobsLoaded: true, jobOnprogress: false };
        }
      }),
    );
  }

  get isJobOnProgress() {
    const jobs = this.getValue().job?.items;
    return (
      jobs &&
      jobs.length > 0 &&
      jobs.filter((job) => job.state === JobState.ONPROGRESS).length > 0
    );
  }

  get isJobOnProgress$() {
    return this.jobs$.pipe(
      map((jobs) => {
        return (
          jobs &&
          jobs.length > 0 &&
          jobs.filter((job) => job.state === JobState.ONPROGRESS).length > 0
        );
      }),
      distinctUntilChanged(),
    );
  }

  isFirstBillInProgress() {
    return this.select((status) => status?.job?.totalCount === 1 && this.isJobOnProgress);
  }

  get recentlyAddedBPIds() {
    const addedJobIds = this.getValue().addedJobIds;
    const job = this.getValue().job;
    if (!addedJobIds?.length) return [];
    const jobs = job?.items?.length ? job.items : [];
    return addedJobIds
      .map((id) => jobs.find((j) => j.id === id))
      .filter((j) => !!j)
      .map((j) => j.bpId)
      .filter((j) => !!j);
  }

  get recentAddedBills() {
    const bills = this.billQuery.getBillsForView(GetBillOption.ALL_BILLS, true);
    return this.recentlyAddedBPIds
      .map((id) => bills.find((b) => b.billPaymentId === id))
      .filter((b) => b);
  }

  getRecentlyAddedBPIds() {
    return this.select(['addedJobIds', 'job']).pipe(
      map(({ addedJobIds, job }) => {
        if (!addedJobIds?.length) return [];
        const jobs = job?.items?.length ? job.items : [];
        return addedJobIds
          .map((id) => jobs.find((j) => j.id === id))
          .filter((j) => !!j)
          .map((j) => j.bpId)
          .filter((j) => !!j);
      }),
    );
  }

  getRecentAddedBills() {
    return this.getRecentlyAddedBPIds().pipe(
      map((ids) => {
        const bills = this.billQuery.getBillsForView(GetBillOption.ALL_BILLS, true);
        return ids.map((id) => bills.find((b) => b.billPaymentId === id)).filter((b) => b);
      }),
    );
  }
}
